import {
  DatabaseRowDataFunctions_getRowDataWithMaxCountsForDatabase,
  DatabaseRowDataFunctions_getRowDataWithMaxCountsForDatabaseWithTagFilter,
  DatabasesByTagQueryGql,
  DatabasesDefaultDirections,
  DatabasesDefaultSortField,
  DatabasesExplorerQueryGrid_QueryResultListNode,
  DatabasesExplorerQueryGrid_QueryResultListNodeWithTagFilter,
  DatabasesExplorerQueryGrid_QueryResults,
  DatabasesExplorerQueryGrid_QueryResultsWithTagFilter,
  DatabasesExplorerQueryGrid_QueryVariables,
  DatabasesExplorerQueryGrid_QueryVariablesWithTagFilter,
  DatabasesExplorerQueryGrid_selectConnectionListResult,
  DatabasesExplorerQueryGrid_selectConnectionListResultWithTagFilter,
  DatabasesExternalAssetsDisplayGridRowData,
  DatabasesQueryGql,
  GraphQLGlobalTypes,
} from '@tableau/data-catalog-client-components';
import { defaultMemoize } from 'reselect';

import { FeatureFlags } from '../../ts/FeatureFlags';
import { ISliceable } from '../../ts/models/ISliceable';
import { catalogApolloClient } from '../CatalogApolloClient';
import { BaseGraphQLResourceQuery } from './BaseGraphQLResourceQuery';
import { getExplorerGraphQLFilters } from './CatalogFilterClauseHelpers';
import { getSortOrderOrDefault } from './CatalogOrderClauseHelpers';
import { makeSliceableFactoryForGraphQLQuery } from './GraphQLSliceable';

const getDefaultSliceableFactorySingleton = defaultMemoize(() => {
  return makeSliceableFactoryForGraphQLQuery<
    DatabasesExplorerQueryGrid_QueryResultListNode,
    DatabasesExternalAssetsDisplayGridRowData,
    DatabasesExplorerQueryGrid_QueryVariables,
    DatabasesExplorerQueryGrid_QueryResults
    >(
    {
      queryGql: DatabasesQueryGql,
      selectConnectionListResult: DatabasesExplorerQueryGrid_selectConnectionListResult,
      mapResultNode: DatabaseRowDataFunctions_getRowDataWithMaxCountsForDatabase,
    },
    catalogApolloClient
  );
});

const getTagFilteredSliceableFactorySingleton = defaultMemoize(() => {
  return makeSliceableFactoryForGraphQLQuery<
    DatabasesExplorerQueryGrid_QueryResultListNodeWithTagFilter,
    DatabasesExternalAssetsDisplayGridRowData,
    DatabasesExplorerQueryGrid_QueryVariablesWithTagFilter,
    DatabasesExplorerQueryGrid_QueryResultsWithTagFilter
    >(
    {
      queryGql: DatabasesByTagQueryGql,
      selectConnectionListResult: DatabasesExplorerQueryGrid_selectConnectionListResultWithTagFilter,
      mapResultNode: DatabaseRowDataFunctions_getRowDataWithMaxCountsForDatabaseWithTagFilter,
    },
    catalogApolloClient
  );
});

export class DatabasesGraphQLResourceQuery
  extends BaseGraphQLResourceQuery<DatabasesExternalAssetsDisplayGridRowData> {

  protected makeSliceable(): ISliceable<DatabasesExternalAssetsDisplayGridRowData> {
    const orderBy = getSortOrderOrDefault(this.getOrder(), DatabasesDefaultSortField, DatabasesDefaultDirections);
    const filterParameters = getExplorerGraphQLFilters(this.getFilters());

    const partialQueryVariables = {
      orderBy: orderBy as GraphQLGlobalTypes.DatabaseSortOrder,
      textSearch: filterParameters.search,
      hasActiveWarning: filterParameters.hasActiveWarning,
      isCertified: filterParameters.isCertified,
      isGroupFFEnabled: FeatureFlags.mergeDedupeDatabasesEnabled,
      publishedConnectionsLineageEnabled: FeatureFlags.publishedConnectionsLineageEnabled,
    };


    const sliceable = filterParameters.tags
      ? getTagFilteredSliceableFactorySingleton()({ tagName: filterParameters.tags, ...partialQueryVariables })
      : getDefaultSliceableFactorySingleton()(partialQueryVariables);

    return sliceable;
  }
}
